import config from 'config';

import { Box, Flex, List, Stack, Text, useTheme } from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  removeAddons,
  removeProvider,
  setAddons,
  setProvider
} from 'store/reducers/bookingAppointmentSlice';

import { ExtendedAddonInterface, ServiceInterface } from 'types';

import { ServiceItem } from '../ServiceList/components';

const {
  booking: { defaultProvider }
} = config;

interface AddonListProps {
  locationId: number;
}

const AddonList = ({ locationId }: AddonListProps) => {
  const dispatch = useAppDispatch();
  const {
    colors: {
      palette: {
        booking: {
          stepService: { addonList }
        }
      }
    }
  } = useTheme();
  const { addons } = useAppSelector((state) => state.services);
  const { selectedServices, selectedAddons } = useAppSelector(
    (state) => state.bookingAppointment
  );

  const currentAddons = addons[locationId] || {};
  const isRenderHeader = selectedServices.length > 1;
  const selectedAddonsArray = Object.values(selectedAddons).flatMap(
    (items) => items
  );

  const updateAddons = (
    updatedAddons: ExtendedAddonInterface[],
    serviceId: number
  ) => {
    if (updatedAddons.length === 0) {
      dispatch(removeAddons({ service: serviceId }));
    } else {
      dispatch(setAddons({ service: serviceId, addons: updatedAddons }));
    }
  };

  const handleClickAddon = (item: ExtendedAddonInterface) => {
    const isAlreadySelected = selectedAddonsArray.some(
      (addon) => addon.id === item.id
    );
    if (isAlreadySelected) {
      const updatedAddons = selectedAddons[item.parentServiceId].filter(
        (addon) => addon.id !== item.id
      );
      updateAddons(updatedAddons, item.parentServiceId);
      dispatch(removeProvider({ service: item.id }));
    } else {
      const updatedAddons = [
        ...(selectedAddons[item.parentServiceId] || []),
        item
      ];
      updateAddons(updatedAddons, item.parentServiceId);
      dispatch(
        setProvider({
          service: item.id,
          provider: defaultProvider
        })
      );
    }
  };

  const handleClickItem = (item: ExtendedAddonInterface | ServiceInterface) => {
    handleClickAddon(item as ExtendedAddonInterface);
  };

  const renderItems = (items: ExtendedAddonInterface[]) =>
    items.map((addon, index) => {
      const isLastItem = index === items.length - 1;
      return (
        <ServiceItem
          key={addon.id}
          service={addon}
          onClick={handleClickItem}
          isChecked={selectedAddonsArray.some((item) => item.id === addon.id)}
          isLastItem={isLastItem}
          isAddonVariant
        />
      );
    });

  return (
    <Stack as={List} p={0} position='relative' gap='10px' h='100%'>
      {selectedServices.map((item) => {
        const serviceAddons = currentAddons[item.id];

        return serviceAddons.length > 0 ? (
          <Box key={item.id} mb='0'>
            {isRenderHeader && (
              <Box
                py='15px'
                px='18px'
                bg={addonList.headerBg}
                borderTopRadius='4px'
              >
                <Flex alignItems='center' gap='17px'>
                  <Text
                    fontSize='25px'
                    fontWeight='400'
                    color={addonList.headerTextColor}
                    lineHeight={1}
                  >
                    {item.name}
                  </Text>
                </Flex>
              </Box>
            )}
            <Flex gap='0' flexWrap='wrap' mt='4px'>
              {renderItems(serviceAddons)}
            </Flex>
          </Box>
        ) : null;
      })}
    </Stack>
  );
};

export default AddonList;
